export const DEFAULT_CATEGORIES = [
  "Arm Strikes",
  "Arm Strikes Landed",
  "Arm Strikes Missed",
  "Leg Strikes",
  "Leg Strikes Landed",
  "Leg Strikes Missed",
  "Ground Strikes",
  "Ground Strikes Landed",
  "Ground Strikes Missed",
  "Takedowns",
  "Takedowns Landed",
  "Takedowns Missed",
  "Submissions",
  "Submissions Landed",
  "Submissions Missed",
  "Knockdowns",
  "Knockdowns Landed",
  "Knockdowns Missed",
];

/**
 * Parse categories array into structured category objects
 * @param {string[]} categories 
 * @returns {Array<{name: string, landed: string, missed: string}>}
 */
export function parseCategories(categories) {
  const cats = categories && categories.length > 0 ? categories : DEFAULT_CATEGORIES;
  const parsed = [];
  
  for (let i = 0; i < cats.length; i += 3) {
    if (cats[i]) {
      parsed.push({
        name: cats[i],
        landed: cats[i + 1] || `${cats[i]} Landed`,
        missed: cats[i + 2] || `${cats[i]} Missed`,
      });
    }
  }
  
  return parsed;
}

/**
 * Get flat array of all category names (landed and missed)
 * @param {string[]} categories - Array of category strings
 * @returns {string[]}
 */
export function getFlatCategories(categories) {
  const parsed = parseCategories(categories);
  const flat = [];
  
  parsed.forEach(cat => {
    flat.push(cat.landed);
    flat.push(cat.missed);
  });
  
  return flat;
}

/**
 * Calculate percentage string "landed/total" from landed and missed stats
 * @param {Object} params
 * @param {string} params.landed 
 * @param {string} params.missed 
 * @param {Array} params.stats
 * @param {number} [params.round] 
 * @returns {string}
 */
export function calPercentage({ missed, landed, stats, round }) {
  let missed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[missed] || 0);
    }, 0) || 0;
  let landed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[landed] || 0);
    }, 0) || 0;
  
  let output = (landed_total || 0) + "/";
  output += (missed_total || 0) + (landed_total || 0);
  
  return output;
}

/**
 * Calculate percentage string "landed/total" from landed and missed stats
 * @param {Object} params
 * @param {string} params.landed 
 * @param {string} params.missed 
 * @param {Array} params.stats
 * @param {number} [params.round] 
 * @returns {string}
 */
export function calPercentageNumeric({ missed, landed, stats, round }) {
  let missed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[missed] || 0);
    }, 0) || 0;
  let landed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[landed] || 0);
    }, 0) || 0;
  
    const total = missed_total + landed_total;
    if (total === 0) return 0;

  return Math.round((landed_total / total) *100);
}


/**
 * Calculate percentage with format "landed (percentage%)"
 * @param {Object} params
 * @param {string} params.landed - Landed category name
 * @param {string} params.missed - Missed category name
 * @param {Array} params.stats - Stats array
 * @param {number} [params.round] - Optional round filter
 * @returns {string} "landed (percentage%)" format
 */
export function calPercentageWithPercent({ missed, landed, stats, round }) {
  let missed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[missed] || 0);
    }, 0) || 0;
  let landed_total = stats
    ?.filter((r) => r.round === round || !round)
    ?.reduce((prev, curr) => {
      return prev + (curr?.[landed] || 0);
    }, 0) || 0;
  
  let output = (landed_total || 0) + " (";
  output += Math.round(
    ((landed_total || 0) / ((missed_total || 0) + (landed_total || 0))) * 100
  ) || 0;
  output += "%)";
  
  return output;
}

/**
 * Calculate total from a single variable
 * @param {Object} params
 * @param {string} params.variable - Variable name
 * @param {Array} params.stats - Stats array
 * @param {number} [params.round] - Optional round filter
 * @returns {number}
 */
export function calcTotalVariable({ variable, stats, round }) {
  return (
    stats
      ?.filter((r) => r.round === round || !round)
      ?.reduce((prev, curr) => {
        return prev + (curr?.[variable] || 0);
      }, 0) || 0
  );
}

/**
 * Calculate total from two stats
 * @param {Object} params
 * @param {string} params.stat_1 - First stat name
 * @param {string} params.stat_2 - Second stat name
 * @param {Array} params.stats - Stats array
 * @param {number} [params.round] - Optional round filter
 * @returns {number}
 */
export function calTotal({ stat_1, stat_2, stats, round }) {
  return (
    stats
      ?.filter((r) => r.round === round || !round)
      ?.reduce((prev, curr) => {
        return prev + (curr?.[stat_1] || 0) + (curr?.[stat_2] || 0);
      }, 0) || 0
  );
}

/**
 * Calculate total strikes thrown from all strike categories
 * @param {Object} params
 * @param {Array} params.stats 
 * @param {string[]} params.categories
 * @param {number} [params.round] 
 * @returns {number}
 */
export function calcTotalStrikesThrown({ stats, categories, round }) {
  const parsed = parseCategories(categories);
  
  return (
    stats
      ?.filter((r) => r.round === round || !round)
      ?.reduce((prev, curr) => {
        let total = prev;
        parsed.forEach(cat => {

            total += (curr?.[cat.landed] || 0) + (curr?.[cat.missed] || 0);
          
        });
        return total;
      }, 0) || 0
  );
}

/**
 * Calculate total strikes landed from all strike categories
 * @param {Object} params
 * @param {Array} params.stats
 * @param {string[]} params.categories
 * @param {number} [params.round]
 * @returns {number}
 */
export function calcTotalStrikesLanded({ stats, categories, round }) {
  const parsed = parseCategories(categories);
  
  return (
    stats
      ?.filter((r) => r.round === round || !round)
      ?.reduce((prev, curr) => {
        let total = prev;
        parsed.forEach(cat => {
            total += (curr?.[cat.landed] || 0);
        });
        return total;
      }, 0) || 0
  );
}

/**
 * Calculate total strikes landed with percentage
 * @param {Object} params
 * @param {Array} params.stats - Stats array
 * @param {string[]} params.categories - Categories from competition
 * @param {number} [params.round] - Optional round filter
 * @returns {string} "landed (percentage%)" format
 */
export function calcTotalStrikesLandedWithPercent({ stats, categories, round }) {
  const thrown = calcTotalStrikesThrown({ stats, categories, round });
  const landed = calcTotalStrikesLanded({ stats, categories, round });
  
  return landed + " (" + (Math.round((landed / thrown) * 100) || 0) + "%)";
}

/**
 * Generate GFX_CATEGORIES array dynamically from competition categories
 * @param {string[]} categories - Categories from competition
 * @returns {Array<{name: string, stat: Function}>}
 */
export function generateGfxCategories(categories) {
  const parsed = parseCategories(categories);
  
  return parsed.map(cat => ({
    name: cat.name,
    stat: (stats, round) => {
      return calPercentage({
        missed: cat.missed,
        landed: cat.landed,
        stats,
        round,
      });
    },
  }));
}

export function generateGfxCategoriesPercentage(categories) {
  const parsed = parseCategories(categories);
  
  return parsed.map(cat => ({
    name: cat.name,
    stat: (stats, round) => {
      return calPercentageNumeric({
        missed: cat.missed,
        landed: cat.landed,
        stats,
        round,
      });
    },
  }));
}



/**
 * Get categories for data logging - flat array of landed and missed
 * @param {string[]} categories - Categories from competition
 * @returns {string[]}
 */
export function getLoggingCategories(categories) {
  return getFlatCategories(categories);
}

/**
 * Find a category by searching for a keyword in the base name
 * @param {string[]} categories - Categories from competition
 * @param {string} keyword - Keyword to search for (case-insensitive)
 * @returns {{name: string, landed: string, missed: string}|null}
 */
export function findCategoryByName(categories, keyword) {
  const parsed = parseCategories(categories);
  return parsed.find(cat => 
    cat.name.toLowerCase().includes(keyword.toLowerCase())
  ) || null;
}

/**
 * Get stat value for a category's landed stat
 * @param {string[]} categories - Categories from competition
 * @param {string} keyword - Keyword to find the category (e.g., "takedown", "knockdown")
 * @returns {string|null} The "landed" stat name for that category
 */
export function getCategoryLandedStat(categories, keyword) {
  const cat = findCategoryByName(categories, keyword);
  return cat ? cat.landed : null;
}

/**
 * Get stat value for a category's missed stat
 * @param {string[]} categories - Categories from competition
 * @param {string} keyword - Keyword to find the category (e.g., "takedown", "knockdown")
 * @returns {string|null} The "missed" stat name for that category
 */
export function getCategoryMissedStat(categories, keyword) {
  const cat = findCategoryByName(categories, keyword);
  return cat ? cat.missed : null;
}
